import React from 'react';
import styled from 'styled-components';
import Flex from 'Components/Grid/Flex';
import Text from 'Components/Typography/Text';
import { Grid } from '@igooods/ui-kit';
import { useSelector } from 'react-redux';
import { getFullCityInfo } from 'Modules/SearchAddress/selectors';
import { phoneFormat } from 'utils/helpers';
import { FontSize, FontWeight, Transitions } from 'theme';
import { colors } from 'constants/colors';
import { mediaLgUp, mediaMdUp, mediaMdDown, mediaSmDown } from 'theme/helpers/css';
import { WLabel } from 'constants/enums';
import ExternalLink from 'Components/ExternalLink';
import { selectByWL, currentWLabel } from 'utils/wLabel';
import { URL } from 'constants/urlMaps';
import NewSocialsList from 'Components/Footer/NewSocialsList';
import useRootUrlWithSubdomain from 'utils/hooks/useRootUrlWithSubdomain';
import { getTypographyFontStyle } from 'theme/selectors';
import { useSupportBtnWithInvertStyles } from 'utils/hooks';

export const CATALOG_FOOTER_ID = 'CATALOG_FOOTER_ID';
export const FOOTER_CONTAINER_TEST_ID = 'FOOTER_CONTAINER_TEST_ID';
export const FOOTER_CITY_PHONE_WRAPPER_TEST_ID = 'FOOTER_CITY_PHONE_WRAPPER_TEST_ID';
export const FOOTER_LINKS_WRAPPER_TEST_ID = 'FOOTER_LINKS_WRAPPER_TEST_ID';

export const FOOTER_HEIGHT = 92;

const FooterWrapper = styled.footer`
  background-color: ${colors.pineGreen};
  padding-right: 97px;
  padding-left: 43px;
  /* убрать после того, как будет готова сетка */
  ${mediaLgUp`
    margin-left: 270px;
  `}

  ${mediaMdDown`
    padding-right: 114px;
    padding-left: 52px;
  `}
  ${mediaSmDown`
    padding-right: 20px;
    padding-left: 20px;
  `}
`;

const FooterContainer = styled(Grid).attrs({ col: { xs: 2 } })`
  height: ${FOOTER_HEIGHT}px;
  ${mediaMdUp`
    grid-template-columns: 1fr 3fr 1fr
  `}
  ${mediaSmDown`
    height: ${FOOTER_HEIGHT - 10}px;
  `}
`;

const wlColorLinkStyles = selectByWL({
  [WLabel.Default]: `color: ${colors.white};`,
  [WLabel.Globus]: `color: ${colors.greyishBrown};`,
});

const ColorLink = styled(ExternalLink)`
  ${wlColorLinkStyles}
  transition: ${Transitions.opacity};
  ${getTypographyFontStyle(FontSize.s)};

  &:hover {
    opacity: 0.8;
  }
`;

const InnerLink = styled(ColorLink)`
  display: inline-block;
  & + ${ExternalLink} {
    margin-left: 32px;
  }
`;

const AdditionalText = styled(Text)`
  ${getTypographyFontStyle(FontSize.xs)};
  color: ${colors.white};
  opacity: 0.8;
`;

interface IInfoLink {
  name: string;
  url: string;
}

export const igooodsInfoLinks: IInfoLink[] = [
  {
    url: URL.delivery,
    name: 'Доставка и оплата',
  },
  {
    url: `${URL.business}?ref=site_footer`,
    name: 'Для бизнеса',
  },
  {
    url: URL.how_it_works,
    name: 'О компании',
  },
];

const globusInfoLinks: IInfoLink[] = [
  {
    url: URL.delivery,
    name: 'Доставка',
  },
  {
    url: URL.how_it_works,
    name: 'Как это работает',
  },
];

const wlInfoLinks = selectByWL({ [WLabel.Default]: igooodsInfoLinks, [WLabel.Globus]: globusInfoLinks });

const wlPhoneFontWeight = selectByWL({ [WLabel.Default]: FontWeight.semiBold, [WLabel.Globus]: FontWeight.extraBold });

const wlInfoFontWeight = selectByWL({ [WLabel.Default]: FontWeight.semiBold, [WLabel.Globus]: FontWeight.extraBold });

const NewFooter = () => {
  const city = useSelector(getFullCityInfo);
  const { mainRoute } = useRootUrlWithSubdomain();

  useSupportBtnWithInvertStyles();
  return (
    <FooterWrapper data-testid={FOOTER_CONTAINER_TEST_ID} id={CATALOG_FOOTER_ID}>
      <FooterContainer>
        <NewSocialsList />
        <Flex center middle grow hideBefore="md">
          <div data-testid={FOOTER_LINKS_WRAPPER_TEST_ID}>
            {wlInfoLinks.map((link: IInfoLink) => (
              <InnerLink
                key={link.name}
                href={
                  currentWLabel === WLabel.Default && !link.url.includes('https://igooods.ru')
                    ? `${mainRoute}${link.url}`
                    : link.url
                }
                newTab
              >
                <Text size={FontSize.s} fontWeight={wlInfoFontWeight}>
                  {link.name}
                </Text>
              </InnerLink>
            ))}
          </div>
        </Flex>
        <Flex column center bottom>
          <Text size={FontSize.s} fontWeight={wlPhoneFontWeight} data-testid={FOOTER_CITY_PHONE_WRAPPER_TEST_ID}>
            {city?.phone && <ColorLink href={`tel:${city?.phone}`}>{phoneFormat(city.phone)}</ColorLink>}
          </Text>
          <AdditionalText size={FontSize.xs}>10:00–22:00</AdditionalText>
        </Flex>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default NewFooter;
